import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ArrowRight, ArrowLeft, Lightbulb, Compass, GameController, Users,
  Trophy, Storefront, Bell, Lightning, BookOpen, Heart, MapPin, ChartBar,
  Plus, MagnifyingGlass, Sparkle
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

// ═══════════════════════════════════════
//  CONTEXTUAL TOUR
// ═══════════════════════════════════════

const TOUR_DEFINITIONS = {
  discover_page: {
    title: 'Discovery Feed',
    steps: [
      { target: '[data-testid="discover-page"]', title: 'Your Discovery Feed', body: 'Browse open sessions, shop events, and tournaments near you. Filter by type or game.' },
      { target: '[data-testid="filter-sessions"]', title: 'Filter by Type', body: 'Use these tabs to show only sessions, tournaments, or shops.' },
      { target: '[data-testid="activation-checklist"]', title: 'Getting Started', body: 'Complete these tasks to level up your account and get better matches.' },
    ],
  },
  collection_page: {
    title: 'Your Collection',
    steps: [
      { target: '[data-testid="collection-page"]', title: 'Game Collection', body: 'Track games you own, want, or have played. Shops and sessions match based on your collection.' },
      { target: '[data-testid="collection-add-btn"]', title: 'Add Games', body: "Search our database or add custom games that aren't listed yet." },
      { target: '[data-testid="collection-filter-all"]', title: 'Filter by Status', body: 'Filter between owned, wishlist, favorite, and other statuses.' },
    ],
  },
  shop_detail: {
    title: 'Game Shop',
    steps: [
      { target: '[data-testid="shop-detail-page"]', title: 'Shop Profile', body: 'See events, game library, hours, and community info for this shop.' },
      { target: '[data-testid="shop-events-tab"]', title: 'Events', body: 'Browse upcoming game nights, tournaments, and open play sessions.' },
    ],
  },
  league_standings: {
    title: 'League Standings',
    steps: [
      { target: '[data-testid="league-detail"]', title: 'League Overview', body: 'Track standings, seasons, and results for your league.' },
    ],
  },
};

export function ContextualTour({ tourKey, children }) {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    ax().get('/api/onboarding/feature-education')
      .then(r => {
        const state = r.data[`tour_${tourKey}`];
        if (!state || (!state.seen && !state.dismissed)) {
          setDismissed(false);
        }
      })
      .catch(() => setDismissed(false));
  }, [tourKey]);

  const tour = TOUR_DEFINITIONS[tourKey];
  if (!tour || dismissed) return children || null;

  const startTour = () => { setActive(true); setStepIdx(0); };
  const endTour = () => {
    setActive(false);
    setDismissed(true);
    ax().post(`/api/onboarding/feature-education/tour_${tourKey}/seen`).catch(() => {});
  };
  const dismissTour = () => {
    setDismissed(true);
    ax().post(`/api/onboarding/feature-education/tour_${tourKey}/dismiss`).catch(() => {});
  };
  const next = () => { if (stepIdx < tour.steps.length - 1) setStepIdx(stepIdx + 1); else endTour(); };
  const prev = () => { if (stepIdx > 0) setStepIdx(stepIdx - 1); };
  const step = tour.steps[stepIdx];

  return (
    <>
      {children}

      {/* Tour prompt banner */}
      {!active && !dismissed && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-20 left-4 right-4 z-40 max-w-md mx-auto"
          data-testid={`tour-prompt-${tourKey}`}
        >
          <div className="bg-[#0e0e16] border border-[#9D4CDD]/30 rounded-xl p-4 shadow-xl shadow-[#9D4CDD]/5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#9D4CDD]/10 flex items-center justify-center shrink-0">
              <Lightbulb size={20} weight="duotone" className="text-[#9D4CDD]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold">Tour: {tour.title}</p>
              <p className="text-[#888] text-xs">Quick walkthrough of this page</p>
            </div>
            <button onClick={startTour} className="px-3 py-1.5 rounded-lg bg-[#9D4CDD] text-white text-xs font-medium shrink-0" data-testid={`tour-start-${tourKey}`}>
              Start
            </button>
            <button onClick={dismissTour} className="text-[#555] hover:text-white shrink-0">
              <X size={16} />
            </button>
          </div>
        </motion.div>
      )}

      {/* Tour overlay */}
      <AnimatePresence>
        {active && step && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center pb-24 px-4"
            onClick={endTour}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Tour card */}
            <motion.div
              key={stepIdx}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="relative bg-[#0e0e16] border border-white/[0.1] rounded-2xl p-5 w-full max-w-sm shadow-2xl"
              data-testid={`tour-step-${stepIdx}`}
            >
              <button onClick={endTour} className="absolute top-3 right-3 text-[#555] hover:text-white">
                <X size={16} />
              </button>
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb size={16} weight="duotone" className="text-[#9D4CDD]" />
                <span className="text-[10px] text-[#9D4CDD] uppercase tracking-wider font-semibold">
                  Step {stepIdx + 1} of {tour.steps.length}
                </span>
              </div>
              <h3 className="text-white font-bold text-base mb-2">{step.title}</h3>
              <p className="text-[#888] text-sm leading-relaxed mb-4">{step.body}</p>
              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  {tour.steps.map((_, i) => (
                    <div key={i} className={`h-1 rounded-full transition-all ${i === stepIdx ? 'w-4 bg-[#9D4CDD]' : 'w-1.5 bg-white/[0.1]'}`} />
                  ))}
                </div>
                <div className="flex gap-2">
                  {stepIdx > 0 && (
                    <button onClick={prev} className="p-2 rounded-lg bg-white/[0.04] text-[#888] hover:text-white">
                      <ArrowLeft size={14} />
                    </button>
                  )}
                  <button onClick={next} className="px-4 py-2 rounded-lg bg-[#9D4CDD] text-white text-xs font-semibold" data-testid="tour-next">
                    {stepIdx === tour.steps.length - 1 ? 'Done' : 'Next'} <ArrowRight size={12} className="inline ml-0.5" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}


// ═══════════════════════════════════════
//  EMPTY STATE EDUCATION CARDS
// ═══════════════════════════════════════

const EMPTY_STATE_CONFIGS = {
  discover_feed: {
    icon: Compass,
    color: '#00F0FF',
    title: 'Nothing in your feed yet',
    body: 'Sessions, events, and tournaments will show up here as they are posted near you.',
    cta: 'Browse Shops',
    ctaPath: '/shops',
  },
  collections: {
    icon: GameController,
    color: '#9D4CDD',
    title: 'Start your game collection',
    body: 'Track games you own, want, or love. Your collection helps match you with sessions and alerts you when favorites get events.',
    cta: 'Add First Game',
    ctaAction: 'add_game',
  },
  leagues: {
    icon: Trophy,
    color: '#F59E0B',
    title: 'No leagues yet',
    body: 'Leagues are competitive seasons at your local shops. Join one or ask your shop to start a league!',
    cta: 'Find Shops',
    ctaPath: '/shops',
  },
  groups: {
    icon: Users,
    color: '#10B981',
    title: 'No recurring groups',
    body: 'Groups are regular meetups for your favorite games. Create one or discover existing groups nearby.',
    cta: 'Explore',
    ctaPath: '/discover',
  },
  notifications: {
    icon: Bell,
    color: '#3B82F6',
    title: 'All caught up!',
    body: "You'll get notifications about events, tournament updates, and collection alerts here.",
    cta: null,
  },
  shop_events: {
    icon: Lightning,
    color: '#F59E0B',
    title: 'No events yet',
    body: 'Create game nights, tournaments, and open play sessions to attract players to your shop.',
    cta: 'Quick Create',
    ctaAction: 'quick_create',
  },
  friends: {
    icon: Users,
    color: '#00F0FF',
    title: 'No friends yet',
    body: 'Find players at sessions and add them as friends to see when they play.',
    cta: 'Find Players',
    ctaPath: '/discover',
  },
  stats: {
    icon: ChartBar,
    color: '#10B981',
    title: 'No stats yet',
    body: 'Play and record matches to build your stats and climb the leaderboard.',
    cta: 'Find Sessions',
    ctaPath: '/discover',
  },
  rules: {
    icon: BookOpen,
    color: '#9D4CDD',
    title: 'Browse game rules',
    body: 'Look up official rules and quick-start guides for hundreds of board games.',
    cta: 'Search Rules',
    ctaAction: 'search',
  },
  shop_library: {
    icon: GameController,
    color: '#10B981',
    title: 'No games in library',
    body: 'Add games to your shop library so players know what you have available for play.',
    cta: 'Add Games',
    ctaAction: 'add_game',
  },
  tournaments: {
    icon: Trophy,
    color: '#F59E0B',
    title: 'No tournaments found',
    body: 'Tournaments are competitive events at local shops. Check back soon or explore shops running them.',
    cta: 'Find Shops',
    ctaPath: '/shops',
  },
};

export function EmptyStateCard({ type, onAction, className = '' }) {
  const config = EMPTY_STATE_CONFIGS[type];
  if (!config) return null;

  const Icon = config.icon;
  const handleClick = () => {
    if (config.ctaPath) {
      window.location.href = config.ctaPath;
    } else if (config.ctaAction && onAction) {
      onAction(config.ctaAction);
    }
  };

  return (
    <div className={`text-center py-10 px-6 ${className}`} data-testid={`empty-state-${type}`}>
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${config.color}10` }}>
        <Icon size={32} weight="duotone" style={{ color: config.color }} />
      </div>
      <h3 className="text-white font-semibold text-base mb-2">{config.title}</h3>
      <p className="text-[#888] text-sm leading-relaxed max-w-xs mx-auto mb-4">{config.body}</p>
      {config.cta && (
        <button onClick={handleClick}
          className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95"
          style={{ backgroundColor: `${config.color}15`, color: config.color }}
          data-testid={`empty-cta-${type}`}
        >
          {config.cta}
        </button>
      )}
    </div>
  );
}


// ═══════════════════════════════════════
//  INLINE HELP TIP
// ═══════════════════════════════════════

export function InlineHelpTip({ featureKey, title, body, icon: CustomIcon }) {
  const [visible, setVisible] = useState(false);
  const Icon = CustomIcon || Lightbulb;

  useEffect(() => {
    ax().get('/api/onboarding/feature-education')
      .then(r => {
        const state = r.data[featureKey];
        if (!state || !state.dismissed) setVisible(true);
      })
      .catch(() => setVisible(true));
  }, [featureKey]);

  const dismiss = () => {
    setVisible(false);
    ax().post(`/api/onboarding/feature-education/${featureKey}/dismiss`).catch(() => {});
  };

  if (!visible) return null;

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className="bg-[#9D4CDD]/[0.04] border border-[#9D4CDD]/15 rounded-xl p-3.5 mb-4 flex items-start gap-3"
      data-testid={`help-tip-${featureKey}`}
    >
      <Icon size={18} weight="duotone" className="text-[#9D4CDD] mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        {title && <p className="text-white text-sm font-medium mb-0.5">{title}</p>}
        <p className="text-[#888] text-xs leading-relaxed">{body}</p>
      </div>
      <button onClick={dismiss} className="text-[#555] hover:text-white shrink-0 mt-0.5">
        <X size={14} />
      </button>
    </motion.div>
  );
}
